import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import { formatLempiras } from '../../lib/format'

interface Tier { id?: number; quantity: number; price_cents: number }
interface PromotionCategory { id: number; name: string }
interface Promotion {
  id: number
  name: string
  strategy: string
  active: boolean
  priority: number
  starts_at: string | null
  ends_at: string | null
  category_id: number | null
  tiers: Tier[]
  category: PromotionCategory | null
  product_count: number
  product_ids: number[]
}
interface Account { id: number; name: string; slug: string }
interface Category { id: number; name: string }
interface SearchProduct { id: number; name: string; sku: string | null }

interface Props {
  promotions: Promotion[]
  categories: Category[]
  account: Account
  can_manage?: boolean
}

export default function PromotionsIndex({ promotions, categories, account }: Props) {
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Promotion | null>(null)
  const [saving, setSaving] = useState(false)
  const [flash, setFlash] = useState<{ notice?: string; alert?: string } | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [active, setActive] = useState(true)
  const [categoryId, setCategoryId] = useState<string>('')
  const [tiers, setTiers] = useState<Tier[]>([{ quantity: 3, price_cents: 0 }])
  const [productIds, setProductIds] = useState<number[]>([])
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<SearchProduct[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedNames, setSelectedNames] = useState<Record<number, string>>({})

  const openNew = () => {
    setEditing(null)
    setName('')
    setActive(true)
    setCategoryId('')
    setTiers([{ quantity: 3, price_cents: 0 }])
    setProductIds([])
    setSelectedNames({})
    setSearch('')
    setResults([])
    setShowForm(true)
  }

  const openEdit = (p: Promotion) => {
    setEditing(p)
    setName(p.name)
    setActive(p.active)
    setCategoryId(p.category_id ? String(p.category_id) : '')
    setTiers(p.tiers.length > 0 ? p.tiers.map(t => ({ id: t.id, quantity: t.quantity, price_cents: t.price_cents })) : [{ quantity: 3, price_cents: 0 }])
    setProductIds(p.product_ids)
    setSelectedNames({})
    setSearch('')
    setResults([])
    setShowForm(true)
  }

  // Búsqueda de productos (server-side, sin N+1 en la página)
  useEffect(() => {
    if (!showForm || categoryId) return
    const q = search.trim()
    if (q.length < 2 && q.length > 0) return
    setSearching(true)
    fetch(`/${account.slug}/promotions/search_products?q=${encodeURIComponent(q)}`)
      .then(r => r.json())
      .then((data: SearchProduct[]) => setResults(data))
      .finally(() => setSearching(false))
  }, [search, showForm, categoryId, account.slug])

  const toggleProduct = (p: SearchProduct) => {
    setProductIds(prev => {
      const has = prev.includes(p.id)
      setSelectedNames(prevNames => {
        const next = { ...prevNames }
        if (has) delete next[p.id]
        else next[p.id] = p.name
        return next
      })
      return has ? prev.filter(id => id !== p.id) : [...prev, p.id]
    })
  }

  const submit = () => {
    if (!name.trim()) return
    setSaving(true)
    const payload = {
      name: name.trim(),
      active: active ? '1' : '0',
      category_id: categoryId || undefined,
      tiers: tiers.filter(t => t.quantity > 0 && t.price_cents >= 0),
      product_ids: categoryId ? [] : productIds,
    }
    const url = `/${account.slug}/promotions${editing ? `/${editing.id}` : ''}`
    const opts = {
      preserveScroll: true,
      onSuccess: (page: any) => {
        const f = (page.props as { flash?: { notice?: string; alert?: string } }).flash
        setFlash(f ?? null)
        setShowForm(false)
        setSaving(false)
      },
      onError: () => setSaving(false),
    }
    if (editing) {
      router.patch(url, payload as any, opts)
    } else {
      router.post(url, payload as any, opts)
    }
  }

  const toggleActive = (p: Promotion) => {
    router.patch(`/${account.slug}/promotions/${p.id}/toggle`, {}, { preserveScroll: true })
  }

  const remove = (p: Promotion) => {
    if (!confirm(`¿Eliminar la promoción "${p.name}"?`)) return
    router.delete(`/${account.slug}/promotions/${p.id}`, { preserveScroll: true })
  }

  return (
    <Layout account={account}>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#202020]">Promociones</h1>
            <p className="text-sm text-gray-500 mt-1">Descuentos automáticos por cantidad (ej. 3x L.899)</p>
          </div>
          <button onClick={openNew} className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded-lg font-semibold hover:bg-[#e6ad06] transition-colors">
            + Nueva promoción
          </button>
        </div>

        {flash?.alert && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{flash.alert}</div>}
        {flash?.notice && <div className="mb-4 rounded-lg bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm">{flash.notice}</div>}

        {promotions.length === 0 && !showForm && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg mb-3">Aún no hay promociones</p>
            <p className="text-sm text-gray-400 mb-4">Crea una promoción por cantidad: define el paquete (ej. 3 unidades por L.899) y los productos participantes.</p>
            <button onClick={openNew} className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded-lg font-semibold hover:bg-[#e6ad06]">+ Crear la primera</button>
          </div>
        )}

        <div className="space-y-3">
          {promotions.map(p => (
            <div key={p.id} className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-48">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-[#202020]">{p.name}</p>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {p.active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {p.tiers.map((t, i) => (
                    <span key={i} className="inline-block bg-[#55ACEE] text-white text-xs px-2 py-0.5 rounded">
                      {t.quantity}u por {formatLempiras(t.price_cents)}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  {p.category ? `Categoría: ${p.category.name}` : `${p.product_count} producto${p.product_count !== 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleActive(p)} className={`px-3 py-1.5 rounded text-sm font-medium ${p.active ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                  {p.active ? 'Desactivar' : 'Activar'}
                </button>
                <button onClick={() => openEdit(p)} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300">Editar</button>
                <button onClick={() => remove(p)} className="px-3 py-1.5 rounded text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100">Eliminar</button>
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#202020]">{editing ? 'Editar promoción' : 'Nueva promoción'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700 text-xl">×</button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Ej: 3x L.899 Camisetas" className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="rounded" />
                  Promoción activa
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Escalones (cantidad → precio del paquete)</label>
                  <div className="space-y-2">
                    {tiers.map((t, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          type="number" min={1} value={t.quantity || ''}
                          onChange={e => setTiers(prev => prev.map((x, xi) => xi === i ? { ...x, quantity: Number(e.target.value) } : x))}
                          placeholder="Cantidad" className="w-24 px-3 py-2 border border-gray-300 rounded"
                        />
                        <span className="text-gray-500">por</span>
                        <input
                          type="number" min={0} step="0.01" value={t.price_cents > 0 ? t.price_cents / 100 : ''}
                          onChange={e => setTiers(prev => prev.map((x, xi) => xi === i ? { ...x, price_cents: Math.round(parseFloat(e.target.value || '0') * 100) } : x))}
                          placeholder="L." className="flex-1 px-3 py-2 border border-gray-300 rounded"
                        />
                        {tiers.length > 1 && (
                          <button onClick={() => setTiers(prev => prev.filter((_, xi) => xi !== i))} className="text-red-500 hover:text-red-700">✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setTiers(prev => [...prev, { quantity: 0, price_cents: 0 }])} className="text-[#55ACEE] hover:underline text-sm mt-2">
                    + Agregar escalón
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aplicar a</label>
                  <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded mb-2">
                    <option value="">Productos específicos…</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name} (toda la categoría)</option>)}
                  </select>

                  {!categoryId && (
                    <div>
                      <input
                        value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar productos…"
                        className="w-full px-3 py-2 border border-gray-300 rounded mb-2"
                      />
                      {searching && <p className="text-xs text-gray-400 mb-2">Buscando…</p>}
                      {results.length > 0 && (
                        <div className="border border-gray-200 rounded max-h-48 overflow-y-auto divide-y divide-gray-100">
                          {results.map(p => (
                            <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                              <input type="checkbox" checked={productIds.includes(p.id)} onChange={() => toggleProduct(p)} className="rounded" />
                              <span className="flex-1 truncate">{p.name}</span>
                              {p.sku && <span className="text-xs text-gray-400">{p.sku}</span>}
                            </label>
                          ))}
                        </div>
                      )}
                      {search.trim().length === 0 && results.length === 0 && !searching && (
                        <p className="text-xs text-gray-400">Escribe para buscar productos (mín. 1 carácter)</p>
                      )}
                      {productIds.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {productIds.map(id => (
                            <span key={id} className="inline-block bg-[#55ACEE] text-white text-xs px-2 py-0.5 rounded">
                              {selectedNames[id] ?? `Producto #${id}`} ✓
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={submit} disabled={saving || !name.trim()} className="flex-1 bg-[#FFC107] text-[#202020] py-2.5 rounded-lg font-semibold hover:bg-[#e6ad06] disabled:opacity-50">
                    {saving ? 'Guardando…' : (editing ? 'Guardar cambios' : 'Crear promoción')}
                  </button>
                  <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancelar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}
